/**
 * Consumer for the v2 SSE finding stream at
 * `POST /api/v2/BIMValidator/findings`.
 *
 * The stream is framed as Server-Sent Events with three event names:
 *   - `event: schema` — one leading frame carrying `event_schema_version`
 *   - `event: finding` — zero or more, one per rule violation
 *   - `event: done` — terminator (may carry an empty JSON payload)
 *
 * We consume it with `fetch` + a manual ReadableStream reader (rather than
 * `EventSource`) because the endpoint is a POST that needs a JSON body and
 * the auth `Authorization` header, neither of which native `EventSource`
 * supports.
 */

import { getApiHost, getAuthHeaders, API_ENDPOINTS } from 'constants/config';
import { FindingEvent } from 'types/findings';
import { serializeRequest } from 'utils/requestSerialization';

export interface FindingStreamRequest {
  Source: string;
  Validator: string;
  Country: string;
  Language: string;
  ElementType: string;
  Content: string;
  ExcludedDocuments?: string[];
}

export interface FindingStreamHandlers {
  onSchema?: (version: string) => void;
  onFinding?: (finding: FindingEvent) => void;
  onDone?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Parse an accumulated SSE buffer into complete frames. Returns the frames
 * and any leftover partial trailing frame so the caller can prepend it to
 * the next chunk.
 */
export function parseSseBuffer(buffer: string): { frames: Array<{ event: string; data: string }>; rest: string } {
  const frames: Array<{ event: string; data: string }> = [];
  let rest = buffer;

  while (true) {
    const separator = rest.indexOf('\n\n');
    if (separator === -1) {
      break;
    }

    const raw = rest.slice(0, separator);
    rest = rest.slice(separator + 2);

    let event = 'message';
    const dataLines: string[] = [];
    for (const line of raw.split('\n')) {
      if (line.startsWith('event:')) {
        event = line.slice(6).trim();
      } else if (line.startsWith('data:')) {
        dataLines.push(line.slice(5).replace(/^ /, ''));
      }
    }

    frames.push({ event, data: dataLines.join('\n') });
  }

  return { frames, rest };
}

/**
 * Stream findings from the v2 endpoint, invoking the supplied handlers as
 * each SSE frame arrives. Resolves when the server closes the stream (or
 * when `event: done` is observed) and rejects if the initial request fails.
 */
export async function streamFindings(
  request: FindingStreamRequest,
  token: string | undefined,
  address: string | null | undefined,
  handlers: FindingStreamHandlers,
  signal?: AbortSignal
): Promise<void> {
  const host = getApiHost(address);

  const response = await fetch(host + API_ENDPOINTS.VALIDATOR_FINDINGS_V2, {
    method: 'POST',
    headers: {
      ...getAuthHeaders(token),
      Accept: 'text/event-stream',
    },
    body: serializeRequest(request),
    signal,
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('UNAUTHORIZED');
    }
    throw new Error(`Findings request failed with status ${response.status}`);
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('Findings response has no readable body');
  }

  const decoder = new TextDecoder();
  let buffer = '';
  let doneEmitted = false;
  const emitDone = () => {
    if (!doneEmitted) {
      doneEmitted = true;
      handlers.onDone?.();
    }
  };

  try {
    while (true) {
      if (signal?.aborted) {
        return;
      }

      const { done, value } = await reader.read();
      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const { frames, rest } = parseSseBuffer(buffer);
      buffer = rest;

      for (const frame of frames) {
        if (frame.event === 'schema') {
          try {
            const parsed = JSON.parse(frame.data);
            handlers.onSchema?.(parsed.event_schema_version);
          } catch (err) {
            handlers.onError?.(err as Error);
          }
        } else if (frame.event === 'finding') {
          try {
            const finding = JSON.parse(frame.data) as FindingEvent;
            handlers.onFinding?.(finding);
          } catch (err) {
            handlers.onError?.(err as Error);
          }
        } else if (frame.event === 'done') {
          emitDone();
          return;
        }
      }
    }

    emitDone();
  } finally {
    try {
      reader.releaseLock();
    } catch {
      // reader may already be released if the stream errored
    }
  }
}
